// 批改細節頁(Feedback)返回檢視紀錄頁的路徑組裝(kanban 106957)。
//
// 定位:這段 query 是「冗餘保險」,不是題目回得來的主因。正常返回時,handleViewRecord
// 進細節頁前存的 teacherState 一路存活(Feedback 頁不碰它),在「返回那次」ClassFeedback
// 初載才由 fetchAllDatas 消耗,並優先還原 selectedChapter — 連下拉選單帶單一章節表格一起
// 還原,讀完即 removeItem。所以不帶 query 也不會落回未選題,題目照樣回來。
//
// query 只有 teacherState 缺席時才生效。最典型:返回後又重新整理檢視紀錄頁 — teacherState
// 已在返回那次被移除,refresh 後只剩 URL,這時 bookId 選回書、chapterName 顯示單一章節表格,
// 避免落回「未選題」(全書章節各一張表)。因此保留、不省。
// (更正:先前註解宣稱 teacherState「進細節頁前就被消耗、不帶 query 就落回未選題」,經 review
//  追查為誤 — 消耗發生在返回那次、且會優先還原章節。此處改為符合實際。)
//
// 欄位語意注意:classrecord(workflowResult 同形)的 chapterID 存的是
// 「章節名稱」— 檢視紀錄頁以 record.chapterID === chapter.chapter 對映章節,
// 所以它對應 URL 的 chapterName;章節 _id(URL 的 chapterId)workflowResult
// 裡沒有,不帶(ClassFeedback 只拿 chapterId 還原下拉選單,缺它仍能以
// chapterName 正確顯示單一章節表格)。
//
// 只做字串/JSON 純邏輯,不碰 DOM 也不打 API — 方便 vitest(node)直測。

import type { classDataProps } from "../types/common";

/**
 * ClassFeedback fetchAllDatas 初載優先還原用的狀態形狀,寫入點目前有兩處:
 * handleViewRecord(進批改細節頁前,四欄位齊寫)與 buildBatchUploadTeacherState
 * (批次上傳送出後,不寫 recordFilter)。ClassFeedback 讀回這裡目前仍以 any 解析、
 * 未套用此型別——收斂讀取端牽動到 recordFilter 既有的 'all' | 'best' | 'first'
 * 字面量型別比對,風險超出本次修正範圍,留待之後另行處理(kanban 106957 code review)。
 */
export interface TeacherState {
  selectBook: string;
  /**
   * 章節「_id」,不是章節名稱——ClassFeedback 用
   * chapters.data.find(ch => ch._id === selectedChapter) 還原下拉選單與單一章節表格。
   * 缺值時整欄不寫入(見 buildBatchUploadTeacherState),讓消費端走原有 fallback。
   */
  selectedChapter?: string;
  selectedClass: classDataProps | null;
  /** handleViewRecord 進批改細節頁前原本會多寫的記錄篩選狀態;此檔的 buildBatchUploadTeacherState 不寫這欄。 */
  recordFilter?: "all" | "best" | "first";
}

export const CORRECTING_RECORDS_PATH = "/teacher/correcting-records";

/**
 * 從 sessionStorage 的 workflowResult 原始字串組出返回路徑。
 * - bookID → bookId、chapterID(=章節名稱) → chapterName,皆 encodeURIComponent
 *   (與 BatchUpload「前往檢視紀錄」同一套查詢字串寫法)
 * - 缺哪個欄位就不帶哪個;整包缺/壞 JSON/欄位非字串 → 裸路徑,照舊回未選題列表,不炸
 */
export const buildCorrectingRecordsReturnPath = (
  workflowResultString: string | null
): string => {
  if (!workflowResultString) return CORRECTING_RECORDS_PATH;

  let parsed: unknown;
  try {
    parsed = JSON.parse(workflowResultString);
  } catch {
    return CORRECTING_RECORDS_PATH;
  }

  const { bookID, chapterID } = (parsed ?? {}) as {
    bookID?: unknown;
    chapterID?: unknown;
  };
  const query: string[] = [];
  if (typeof bookID === "string" && bookID) {
    query.push(`bookId=${encodeURIComponent(bookID)}`);
  }
  if (typeof chapterID === "string" && chapterID) {
    query.push(`chapterName=${encodeURIComponent(chapterID)}`);
  }
  return query.length
    ? `${CORRECTING_RECORDS_PATH}?${query.join("&")}`
    : CORRECTING_RECORDS_PATH;
};

/**
 * BookPreview「上傳班級作文」按鈕組出的 /batch-upload 導頁參數(kanban 106957 code review)。
 * - 抽出這段純邏輯是因為上一版曾把 chapter 物件裡「名稱」與「_id」兩個欄位的用途搞反
 *   (見 buildBatchUploadTeacherState 的完整說明)——組參數這段若繼續內嵌在元件裡,
 *   之後有人不小心改錯來源不會有任何測試失敗,只會靜默退回 fallback 行為。
 * - chapterId 傳章節「名稱」(chapter.chapter)——後端 prompt/紀錄以章節名為 key
 *   (同單篇 ticket,勿改),BatchUpload.tsx 送出批改時仍讀這個 query key。
 * - chapterUid 傳章節「_id」(chapter._id)——不是 chapterId 的用途,只給
 *   BatchUpload.tsx 轉手帶進 buildBatchUploadTeacherState 的 chapterUid 參數,
 *   最終落到 ClassFeedback 用 _id 比對還原下拉選單。
 * - chapterName 目前與 chapterId 同值(都是章節名稱),保留是因為 BatchUpload.tsx
 *   目前分開讀兩個 query key;不在這裡做去重合併,以免動到既有讀取端。
 * - 純字串組裝,不做防禦性判斷——bookId/chapter 是否有值由呼叫端保證,與既有呼叫點
 *   的行為一致。
 */
export const buildBatchUploadNavParams = (
  bookId: string,
  chapter: { _id: string; chapter: string }
): Record<string, string> => ({
  bookId,
  chapterId: chapter.chapter,
  chapterName: chapter.chapter,
  chapterUid: chapter._id,
});

/**
 * 批次上傳精靈 Step 6「送出批改」→「前往檢視紀錄」組出的 teacherState(kanban 106957)。
 * - 與 ClassFeedback fetchAllDatas 消耗的 teacherState 同形(selectBook/selectedChapter/
 *   selectedClass),寫進 sessionStorage 後由該頁初載優先還原,直接帶入剛上傳的班級頁籤
 *   與題目下拉選單,不必等 URL query 這條保險生效。
 * - chapterUid 要傳章節「_id」,不是 BatchUpload 內給後端 prompt/紀錄當 key 用的
 *   chapterId(章節名稱)。上一版誤把 chapterId(名稱)塞進 selectedChapter,
 *   讓 ClassFeedback 的 chapters.data.find(ch => ch._id === selectedChapter) 永遠
 *   比對失敗、chapterMap 變空陣列,題目下拉空白且整張表格顯示「無記錄」——比不修還糟
 *   (code review 抓到的阻斷性 bug)。
 * - chapterUid 缺值(空字串)時,回傳物件不含 selectedChapter 這個欄位,而不是寫入空
 *   字串——空字串本身是 falsy,ClassFeedback 的 savedState?.selectedChapter 判斷式
 *   並不會因此誤判(會誤判的是「沿用 chapterId 章節名稱」這種非空字串)。不寫入該欄位
 *   單純是語意更明確:代表「這個值本來就沒有」,不依賴消費端剛好用 falsy 判斷,避免未來
 *   讀取端邏輯改變(例如改用 `in` 判斷欄位是否存在)時失效。
 *   典型觸發情境:舊的、還沒帶 chapterUid 參數的分享連結。
 * - selectedClass 可能是 null(理論上批次上傳送出前必選班級,這裡仍保留防禦):
 *   呼叫端應在 selectedClass 存在時才寫入 sessionStorage,null 時略過寫入、
 *   維持原本的 URL query fallback,不讓按鈕壞掉。
 * - 不驗證 chapterUid 對應的章節是否還存在(既有 savedState.selectedChapter 機制的
 *   既有缺口,本次不修);也不處理 refresh 後的持久化,交給 URL query fallback。
 */
export const buildBatchUploadTeacherState = (params: {
  bookId: string;
  chapterUid: string;
  selectedClass: classDataProps | null;
}): TeacherState => {
  const state: TeacherState = {
    selectBook: params.bookId,
    selectedClass: params.selectedClass,
  };
  if (params.chapterUid) {
    state.selectedChapter = params.chapterUid;
  }
  return state;
};
